/**
 * EntityDetailCompact - Compact entity detail for document panel
 *
 * Shows entity details with evidence quotes.
 * Designed for use in the UnifiedDocumentPanel detail view.
 */

import { useState } from 'react'
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { type Document2Entity } from '@/lib/api/client'
import { getEntityDomain, DOMAIN_CONFIG } from '@/lib/constants/entityDomainConfig'

interface EntityDetailCompactProps {
  entity: Document2Entity
  onViewInDocuments3?: () => void
}

export function EntityDetailCompact({
  entity,
  onViewInDocuments3,
}: EntityDetailCompactProps) {
  const [showAllEvidence, setShowAllEvidence] = useState(false)
  const domain = getEntityDomain(entity.entityType)
  const config = DOMAIN_CONFIG[domain]

  const displayedEvidence = showAllEvidence
    ? entity.evidence
    : entity.evidence.slice(0, 3)
  const hasMoreEvidence = entity.evidence.length > 3

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Badge className={`${config.bgColor} ${config.textColor} border-0 text-xs`}>
            {entity.entityType}
          </Badge>
          <Badge
            variant={entity.status === 'created' ? 'success' : 'warning'}
            title={entity.status === 'created'
              ? 'In Graph — stored in Fuseki knowledge graph with a permanent URI'
              : 'Pending — extracted by AI, awaiting review'}
          >
            {entity.status === 'created' ? 'In Graph' : 'Pending'}
          </Badge>
        </div>
        <h4 className="font-semibold text-text-primary text-sm">{entity.name}</h4>
      </div>

      {/* Description */}
      {entity.description && (
        <p className="text-xs text-text-secondary leading-relaxed">
          {entity.description}
        </p>
      )}

      {/* Confidence */}
      <div className="flex items-center gap-2 text-xs text-text-muted">
        <span>Confidence:</span>
        <span className="font-medium">{Math.round((entity.confidence || 0.8) * 100)}%</span>
      </div>

      {/* Evidence */}
      <div>
        <h5 className="text-xs font-medium text-text-secondary mb-2">
          Evidence ({entity.evidence.length})
        </h5>
        <div className="space-y-2">
          {displayedEvidence.map((ev, i) => (
            <div
              key={ev.id || i}
              className="bg-solution/5 border-l-2 border-solution p-2 rounded-r text-xs"
            >
              <p className="italic text-text-primary leading-relaxed">"{ev.quote}"</p>
            </div>
          ))}
        </div>

        {hasMoreEvidence && (
          <button
            onClick={() => setShowAllEvidence(!showAllEvidence)}
            className="flex items-center gap-1 text-xs text-solution hover:text-solution/80 mt-2"
          >
            {showAllEvidence ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Show {entity.evidence.length - 3} more
              </>
            )}
          </button>
        )}
      </div>

      {/* View in Documents3 link */}
      {onViewInDocuments3 && (
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-xs"
          onClick={onViewInDocuments3}
        >
          <ExternalLink className="h-3 w-3" />
          View in Documents3
        </Button>
      )}
    </div>
  )
}
